import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { RoleManagerDialog } from "../../components/admin/RoleManagerDialog";

/* 角色管理（/admin/roles）—— 系统管理 → 用户管理 → 角色管理
 *
 * 需求背景：角色权限页进入后应先展示岗位清单，管理员选择某个岗位后再集中配置该岗位，
 * 避免默认同时铺开岗位列表和完整编辑表单造成信息拥挤。岗位详情写入 `?role=`，
 * 使刷新、直达和浏览器后退都能保留清晰的列表 / 详情层级；具体权限数据仍由原有接口维护。
 *
 * 与账号列表的关系：角色编辑里点某个账号会跳到 /admin/users?user=<username>，
 * 由账号列表页自动打开该账号的编辑弹窗（保持原来「从角色点账号直接改人」的动线）。
 *
 * 布局：embedded 面板使用 `.card-fill` 填充内容区剩余高度，因此页面容器必须加 `.page-fill`；
 *      这样列表和详情各自的内部滚动区域都有稳定的可用高度，不会依赖视口减法常量。
 */
export default function RolesManager() {
  const { lang, api, loadPerms } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedRoleKey = new URLSearchParams(location.search).get("role");

  const rolePath = (key) => {
    const params = new URLSearchParams(location.search);
    if (key) params.set("role", key);
    else params.delete("role");
    const search = params.toString();
    return `${location.pathname}${search ? `?${search}` : ""}${location.hash}`;
  };

  const onRoleChange = (key, { replace = false, returnToList = false } = {}) => {
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    // 复制草稿从岗位详情发起，但「返回岗位列表」应回到清单，而不是原岗位编辑页。
    const returnPath = returnToList ? rolePath(null) : currentPath;
    navigate(rolePath(key), {
      replace,
      state: key ? (replace ? location.state : { roleManagerReturnPath: returnPath }) : null,
    });
  };

  const onRoleBack = () => {
    const returnPath = location.state?.roleManagerReturnPath;
    const fallbackPath = rolePath(null);
    const target = typeof returnPath === "string" && /^\/admin\/roles(?:[?#]|$)/.test(returnPath)
      ? returnPath
      : fallbackPath;
    navigate(target, { replace: true, state: null });
  };

  return (
    <div className="space-y-4 page-fill" data-testid="admin-roles-page">
      <RoleManagerDialog
        lang={lang}
        api={api}
        embedded
        initialRoleKey={selectedRoleKey}
        onRoleChange={onRoleChange}
        onRoleBack={onRoleBack}
        onChanged={() => { loadPerms && loadPerms(); }}
        onEditUser={(un) => window.location.assign(`/admin/users?user=${encodeURIComponent(un)}`)}
      />
    </div>
  );
}
